import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { forkJoin } from "rxjs";
import { OrgLevelTypeEnum } from "src/app/enums/orgLevelType.enum";
import { Lookup } from "src/app/models/lookup.model";
import { OrganizationLevel } from "src/app/models/organizationLevel.model";
import { OrganizationLevelService } from "src/app/services/organizationLevel.service";
import { TypeService } from "src/app/services/type.service";
import {
    FormComponent,
    IFormDialogData,
} from "src/app/shared/form/form.component";
import { MatSelectChange } from "@angular/material/select";
import {
    CheckboxFormField,
    FormField,
    NumberFormField,
    SingleSelectionFormField,
    TextFormField,
} from "src/app/shared/form/formField.model";
import { ColumnSpan } from "src/app/shared/form/columnSpan.model";
import nameOf from "src/app/shared/form/nameOf.helper";
import { LATITUDE_RANGE, LONGITUDE_RANGE } from "src/app/shared/constants/coordinates.constants";
import { isOutOfRange } from "src/app/shared/utils/coordinates.util";

const LONE_COORDINATE_MESSAGE = "Both Latitude and Longitude must be provided together.";
const LATITUDE_PROPERTY = nameOf<OrganizationLevel>((x) => x.latitude);
const LONGITUDE_PROPERTY = nameOf<OrganizationLevel>((x) => x.longitude);

export interface IOrganizationLevelDialogData extends IFormDialogData {
    parentId?: string;
}

@Component({
  standalone: false,
    selector: "app-organization-level-dialog",
    templateUrl: "./../../shared/form/form.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./../../shared/form/form.component.scss"],
})
export class OrganizationLevelDialogComponent
    extends FormComponent<OrganizationLevel>
    implements OnInit
{
    organizationLevels: Lookup[];

    constructor(
        dialogRef: MatDialogRef<OrganizationLevelDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: IOrganizationLevelDialogData,
        private organizationLevelService: OrganizationLevelService,
        private typeService: TypeService,
        protected injector: Injector,
    ) {
        super(
            dialogRef,
            organizationLevelService,
            data,
            "Organization",
            injector,
        );
    }

    ngOnInit(): void {
        this.isFormLoading = true;
        forkJoin({
            entity: this.getEntity(),
            organizationLevels: this.organizationLevelService.getAll(),
        }).subscribe(
            (result) => {
                this.entity = result.entity;
                this.organizationLevels = result.organizationLevels;
                this.initModel();
                this.initFields();
                this.isFormLoading = false;
            },
            (error) => {
                this.toastService.showError(error);
                this.isFormLoading = false;
            },
        );
    }

    getNewObject(): OrganizationLevel {
        return new OrganizationLevel();
    }

    validate(): boolean {
        return true;
    }

    initModel() {
        this.entity.parentId = this.data.parentId;
    }

    onBeforeSave(model: OrganizationLevel): void {
        // Only a Site has a physical location; the API rejects coordinates on any other type.
        if (model.orgLevelType !== OrgLevelTypeEnum.Site) {
            model.latitude = null;
            model.longitude = null;
        }
    }

    initFields(): void {
        this.fields = [
            new SingleSelectionFormField({
                property: nameOf<OrganizationLevel>((x) => x.parentId),
                options: this.organizationLevels,
                name: "Parent Organization",
                required: false,
            }),
            new TextFormField({
                property: nameOf<OrganizationLevel>((x) => x.name),
                name: "Organization Name",
            }),
            new SingleSelectionFormField({
                property: nameOf<OrganizationLevel>((x) => x.orgLevelType),
                options: this.typeService.getEnumOptions(OrgLevelTypeEnum),
                name: "Organization Type",
                onChange: (change: MatSelectChange) => this.onOrgLevelTypeChange(change.value),
            }),
            new NumberFormField({
                property: LATITUDE_PROPERTY,
                name: "Latitude",
                required: typeof this.entity.longitude === "number",
                min: LATITUDE_RANGE.min,
                max: LATITUDE_RANGE.max,
                isVisible: () => this.entity.orgLevelType === OrgLevelTypeEnum.Site,
                onChange: (event: Event, field: FormField) => this.requireCoordinatePair(event, field),
                validator: (value: unknown) =>
                    isOutOfRange(value, LATITUDE_RANGE) ? "Latitude must be between -90 and 90."
                        : this.isLoneCoordinate(value, this.entity.longitude) ? LONE_COORDINATE_MESSAGE : null,
            }),
            new NumberFormField({
                property: LONGITUDE_PROPERTY,
                name: "Longitude",
                required: typeof this.entity.latitude === "number",
                min: LONGITUDE_RANGE.min,
                max: LONGITUDE_RANGE.max,
                isVisible: () => this.entity.orgLevelType === OrgLevelTypeEnum.Site,
                onChange: (event: Event, field: FormField) => this.requireCoordinatePair(event, field),
                validator: (value: unknown) =>
                    isOutOfRange(value, LONGITUDE_RANGE) ? "Longitude must be between -180 and 180."
                        : this.isLoneCoordinate(value, this.entity.latitude) ? LONE_COORDINATE_MESSAGE : null,
            }),
            new CheckboxFormField({
                property: nameOf<OrganizationLevel>(
                    (x) => x.enableLabResultExceedanceNotifications,
                ),
                name: "Enable Lab Result Exceedance Alerts",
                required: false,
                columnSpan: new ColumnSpan(1, 1),
                isVisible: () =>
                    this.entity.orgLevelType === OrgLevelTypeEnum.Site,
            }),
        ];
    }

    private onOrgLevelTypeChange(orgLevelType: OrgLevelTypeEnum): void {
        if (orgLevelType !== OrgLevelTypeEnum.Site) {
            this.entity.latitude = null;
            this.entity.longitude = null;
            this.fields
                .filter((field) => field.property === LATITUDE_PROPERTY || field.property === LONGITUDE_PROPERTY)
                .forEach((field) => (field.required = false));
        }
    }

    // The API rejects a Site with only one coordinate, so once one is entered the other becomes required.
    // The typed value is read from the input event, which fires before ngModel has updated the entity.
    private requireCoordinatePair(event: Event, field: FormField): void {
        const isLatitude = field.property === LATITUDE_PROPERTY;
        const otherProperty = isLatitude ? LONGITUDE_PROPERTY : LATITUDE_PROPERTY;
        const otherValue = isLatitude ? this.entity.longitude : this.entity.latitude;
        const otherField = this.fields.find((candidate) => candidate.property === otherProperty);
        if (otherField) {
            otherField.required = (event.target as HTMLInputElement).value !== "";
        }
        field.required = typeof otherValue === "number";
    }

    private isLoneCoordinate(value: unknown, otherValue: unknown): boolean {
        return typeof value !== "number" && typeof otherValue === "number";
    }
}
